import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const STATUS_CLS = { draft: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', superseded: 'bg-zinc-100 text-zinc-600' };
const today = () => new Date().toISOString().slice(0, 10);

// Admin: Temple View Capital Sales Tool versions + daily DSCR base rate (Wholesale Broker delivery).
export const TvcAdminPanel = () => {
  const [status, setStatus] = useState(null);
  const [ruleSets, setRuleSets] = useState([]);
  const [rates, setRates] = useState({ current: null, history: [] });
  const [rate, setRate] = useState({ rate_percent: '', effective_date: today(), source: 'TVC daily pricing email' });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [s, rs, br] = await Promise.all([api.get('/pricing/tvc/status'), api.get('/pricing/tvc/rule-sets'), api.get('/pricing/tvc/base-rates')]);
    setStatus(s.data); setRuleSets(rs.data.rule_sets); setRates(br.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try {
      const r = await api.post('/pricing/tvc/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`TVC ${r.data.version} imported as DRAFT — review and approve`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const approve = async (rs) => {
    try { await api.post(`/pricing/tvc/rule-sets/${rs.rule_set_id}/approve`); toast.success(`TVC ${rs.version} approved — previous version superseded`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
  };

  const addRate = async () => {
    if (!rate.rate_percent) return toast.error('Enter the base rate as a percent, e.g. 5.875');
    try {
      await api.post('/pricing/tvc/base-rates', { ...rate, rate_percent: Number(rate.rate_percent), product: 'dscr' });
      toast.success('DSCR base rate saved'); setRate({ ...rate, rate_percent: '' }); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const cur = rates.current;
  return (
    <section className="border rounded-xl p-4 space-y-4" data-testid="tvc-admin-section">
      <div>
        <h2 className="font-semibold">Temple View Capital — Sales Tool engine (Wholesale Broker)</h2>
        <p className="text-xs text-muted-foreground">Upload each new TVC Sales Tool version; constants (tier tables, grids, fee schedule, state rules) are extracted and used only after admin approval. Every quote is labeled “Calculated (preliminary) — subject to lender verification and approval”.</p>
      </div>
      {status?.warnings?.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1" data-testid="tvc-warnings">
          {status.warnings.map((w) => <p key={w} className="flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{w}</p>)}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="tvc-rule-sets">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium">Workbook versions</h3>
            <label className="inline-flex">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="tvc-upload-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="tvc-upload-btn"><Upload className="h-4 w-4 mr-1" />{busy ? 'Importing…' : 'Import TVC .xlsx'}</Button>
            </label>
          </div>
          {ruleSets.length === 0 && <p className="text-sm text-muted-foreground">No TVC workbook imported yet.</p>}
          {ruleSets.map((rs) => (
            <div key={rs.rule_set_id} className="border rounded-lg p-2 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`tvc-rule-set-${rs.rule_set_id}`}>
              <div>
                <p className="font-medium">{rs.version} <span className="text-muted-foreground font-normal">· {rs.source_filename}</span></p>
                <p className="text-xs text-muted-foreground">Imported {new Date(rs.imported_at).toLocaleString()}{rs.approved_at ? ` · approved ${new Date(rs.approved_at).toLocaleDateString()} by ${rs.approved_by_name || 'admin'}` : ''} · TVC transaction fee (UW + doc/legal + ops + tax svc): DSCR ${(rs.constants?.fees?.dscr_underwriting_fee || 0) + (rs.constants?.fees?.document_legal_fee || 0) + (rs.constants?.fees?.operations_support_fee || 0) + (rs.constants?.fees?.tax_service_fee || 0)}, RTL ${(rs.constants?.fees?.rtl_bridge_underwriting_fee || 0) + (rs.constants?.fees?.document_legal_fee || 0) + (rs.constants?.fees?.operations_support_fee || 0) + (rs.constants?.fees?.tax_service_fee || 0)} · custom closing fees (WBCF) excluded by AXE policy</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={STATUS_CLS[rs.status]}>{rs.status}</Badge>
                {rs.status === 'draft' && <Button size="sm" onClick={() => approve(rs)} data-testid={`tvc-approve-${rs.rule_set_id}`}><CheckCircle className="h-4 w-4 mr-1" />Approve</Button>}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2" data-testid="tvc-base-rate">
          <h3 className="text-sm font-medium">DSCR daily base rate (TVC cell V11)</h3>
          <div className={`rounded-lg p-3 text-sm ${cur ? (cur.stale ? 'bg-red-50 border border-red-200' : cur.not_today ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200') : 'bg-muted'}`} data-testid="tvc-current-rate">
            {cur ? <><span className="text-2xl font-semibold">{cur.rate_percent}%</span> <span className="text-xs text-muted-foreground">effective {cur.effective_date} · {cur.source} · by {cur.entered_by_name || 'admin'}{cur.stale ? ' · OUTDATED — quotes blocked' : cur.not_today ? ' · not entered today' : ''}</span></> : 'No base rate on file — DSCR quotes are blocked.'}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs text-muted-foreground">Rate (%)</label><Input type="number" step="0.125" value={rate.rate_percent} onChange={(e) => setRate({ ...rate, rate_percent: e.target.value })} data-testid="tvc-rate-input" /></div>
            <div><label className="text-xs text-muted-foreground">Effective date</label><Input type="date" value={rate.effective_date} onChange={(e) => setRate({ ...rate, effective_date: e.target.value })} data-testid="tvc-rate-date" /></div>
            <div><label className="text-xs text-muted-foreground">Source</label><Input value={rate.source} onChange={(e) => setRate({ ...rate, source: e.target.value })} data-testid="tvc-rate-source" /></div>
          </div>
          <Button size="sm" onClick={addRate} data-testid="tvc-rate-save-btn">Save today's base rate</Button>
          {rates.history.length > 0 && (
            <div className="text-xs text-muted-foreground max-h-32 overflow-auto border rounded-lg p-2" data-testid="tvc-rate-history">
              {rates.history.map((h) => <p key={h.rate_id}>{h.effective_date} · {h.rate_percent}% · {h.source} · {h.entered_by_name || 'admin'} · {new Date(h.entered_at).toLocaleString()}</p>)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TvcAdminPanel;
